import React, { useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table2, Plus, Trash2, UserPlus, UserMinus, RefreshCw } from 'lucide-react';

export default function AdminTables() {
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(null); // table_id
  const [form, setForm] = useState({ name: '', capacity: 8, notes: '' });

  const load = () => {
    Promise.all([
      apiGet('/api/tables').then(r => setTables(r.data.tables || [])),
      apiGet('/api/guests').then(r => setGuests(r.data.guests || [])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/api/tables', form);
      setShowCreate(false);
      setForm({ name: '', capacity: 8, notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Handlingen mislykkedes'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Slet dette bord?')) return;
    try {
      await apiDelete(`/api/tables/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke slette bordet');
    }
  };

  const handleAssign = async (tableId, guestId) => {
    try {
      await apiPost(`/api/tables/${tableId}/assign/${guestId}`, {});
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Handlingen mislykkedes'); }
  };

  const handleUnassign = async (tableId, guestId) => {
    try {
      await apiPost(`/api/tables/${tableId}/unassign/${guestId}`, {});
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke fjerne gæsten fra bordet');
    }
  };

  const unassignedGuests = guests.filter(g => !g.table_id);

  return (
    <div className="animate-fade-in" data-testid="admin-tables-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Bordplacering</h1>
          <p className="text-sm text-slate-400 mt-1">{tables.length} borde, {unassignedGuests.length} gæster uden bord</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-tables-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => window.print()} className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
            Print
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="create-table-btn">
            <Plus className="w-4 h-4 mr-1" /> Nyt bord
          </Button>
        </div>
      </div>

      {loading ? <p className="text-slate-400">Indlæser...</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tables.map(table => (
            <div key={table.id} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5" data-testid={`table-card-${table.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Table2 className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-base font-medium text-slate-200" style={{ fontFamily: "'Outfit', sans-serif" }}>{table.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">{(table.guests || []).length}/{table.capacity}</span>
                  <button onClick={() => setShowAssign(table.id)} className="p-1 rounded hover:bg-blue-500/10 text-slate-400 hover:text-blue-400" data-testid={`assign-btn-${table.id}`}>
                    <UserPlus className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(table.id)} className="p-1 rounded hover:bg-red-500/10 text-slate-400 hover:text-red-400" data-testid={`delete-table-${table.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {table.notes && <p className="text-xs text-slate-500 mb-3">{table.notes}</p>}
              <div className="space-y-1.5">
                {(table.guests || []).length === 0 ? (
                  <p className="text-xs text-slate-600 italic">Ingen gæster tildelt</p>
                ) : (table.guests || []).map(g => (
                  <div key={g.id} className="flex items-center justify-between py-1.5 px-2 bg-slate-900/30 rounded text-xs">
                    <span className="text-slate-300">{g.full_name}</span>
                    <button onClick={() => handleUnassign(table.id, g.id)} className="text-slate-500 hover:text-red-400" data-testid={`unassign-${g.id}`}>
                      <UserMinus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {tables.length === 0 && <p className="text-slate-500 text-sm col-span-full text-center py-8">Der er endnu ikke oprettet borde.</p>}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-md">
          <DialogHeader><DialogTitle className="text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>Nyt bord</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="create-table-form">
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Bordnavn *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none" data-testid="table-name-input" required />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Kapacitet</label>
              <input type="number" min={1} max={50} value={form.capacity} onChange={e => setForm(f => ({...f, capacity: parseInt(e.target.value) || 8}))}
                className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none" data-testid="table-capacity-input" />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Noter</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none" data-testid="table-notes-input" />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">Annuller</Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white" data-testid="table-save-btn">Opret</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={!!showAssign} onOpenChange={() => setShowAssign(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-md">
          <DialogHeader><DialogTitle className="text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>Tildel gæst</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="assign-guest-list">
            {unassignedGuests.length === 0 ? (
              <p className="text-sm text-slate-500">Alle gæster er tildelt et bord.</p>
            ) : unassignedGuests.map(g => (
              <button key={g.id} onClick={() => { handleAssign(showAssign, g.id); setShowAssign(null); }}
                className="w-full flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-left"
                data-testid={`assign-guest-${g.id}`}>
                <div>
                  <p className="text-sm text-slate-200">{g.full_name}</p>
                  <p className="text-xs text-slate-500 font-mono">{g.display_email || g.email || g.private_email || g.corporate_email}</p>
                </div>
                <UserPlus className="w-4 h-4 text-blue-400" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
